import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Ticket, ArrowRight } from "lucide-react";
import { NAV, DEPARTMENTS } from "@/lib/data";
import AirZimbabweLogo from "@/components/AirZimbabweLogo";

const PromoStrip = () => (
  <Link
    to="/offers/london-harare"
    className="group block bg-midnight text-linen border-b border-ochre/40 hover:bg-terracotta transition-colors duration-300"
    data-testid="header-promo-strip"
  >
    <div className="container-edge flex items-center justify-center gap-3 py-2 text-center">
      <Ticket className="w-3.5 h-3.5 stroke-1 text-ochre group-hover:text-linen transition-colors" aria-hidden="true" />
      <span className="text-[11px] md:text-[12px] tracking-wide leading-tight">
        <strong className="tracking-[0.15em] uppercase text-ochre group-hover:text-linen mr-2 text-[10px] md:text-[11px]">
          Time Saver Promo
        </strong>
        <span className="hidden sm:inline">Non-stop London&nbsp;Gatwick&nbsp;→&nbsp;Harare from </span>
        <span className="sm:hidden">LGW → HRE from </span>
        <strong className="text-linen">£515</strong>
        <span className="mx-2 opacity-40">·</span>
        <span className="hidden md:inline">travel 15 Jul – 15 Nov 2026</span>
        <span className="md:hidden">Jul – Nov ’26</span>
      </span>
      <ArrowRight className="w-3.5 h-3.5 stroke-1 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
    </div>
  </Link>
);

const SimbaMark = () => (
  <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
    <AirZimbabweLogo className="h-10 md:h-12 w-auto" />
    <span className="hidden md:block h-8 w-px bg-ink/20" aria-hidden="true" />
    <span
      className="hidden md:block text-[9px] tracking-[0.24em] uppercase text-muted2 leading-tight max-w-[90px]"
      data-testid="header-airzim-badge"
    >
      Official UK<br />GSA
    </span>
  </Link>
);

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const sales = DEPARTMENTS.find((d) => d.id === "agency-sales");

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-linen/95 backdrop-blur-xl border-b border-ink/5"
          : "bg-linen/80 backdrop-blur-md"
      }`}
      data-testid="site-header"
    >
      <PromoStrip />
      <div className={`container-edge flex items-center justify-between gap-6 ${scrolled ? "py-1" : "py-2"}`}>
        <SimbaMark />

        <nav className="hidden lg:flex items-center gap-7" data-testid="primary-nav">
          {NAV.filter((n) => n.to !== "/corporate-groups").map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `text-[13px] tracking-wide hover:text-terracotta transition-colors ${
                  isActive ? "text-terracotta" : "text-ink"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={`tel:${sales.phone}`}
            data-testid="header-call-sales"
            className="flex items-center gap-2 text-[13px] hover:text-terracotta transition-colors"
          >
            <Phone className="w-4 h-4 stroke-1" />
            <span>{sales.phoneIntl}</span>
          </a>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-linen border-t border-ink/10 mt-2" data-testid="mobile-nav">
          <div className="container-edge py-6 flex flex-col gap-4">
            {NAV.filter((n) => n.to !== "/corporate-groups").map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `text-base py-1 ${isActive ? "text-terracotta" : "text-ink"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a href={`tel:${sales.phone}`} className="btn-accent w-fit mt-2" data-testid="mobile-call-sales">
              <Phone className="w-4 h-4" /> {sales.phoneIntl}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
